import React, { FC, useMemo } from 'react';
import Arrow from '../../../assets/icons/arrow-right.svg?react';
import DoubleArrow from '../../../assets/icons/double-arrow-right.svg?react';
import styles from './AppPagination.module.scss';
import formStyles from '../table/AppTable.module.scss';
import clsx from 'clsx';
import { Chip } from './Chip.tsx';
import {
  Pagination,
  PaginationItem,
  PaginationRenderItemParams,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export type AppPaginationProps = {
  page: number;
  perPage: number;
  totalCount: number;
  pageSizes?: number[];
  changePagination: (pagination: { page?: number; perPage?: number }) => void;
  numberOfRows?: number;
  customClassname?: string;
};

const defineTestId = (info: PaginationRenderItemParams) => {
  switch (info.type) {
    case 'previous':
      return 'pagination-previous';
    case 'page':
      return `page-number-${info.page}`;
    case 'next':
      return 'pagination-next';
    default:
      return '';
  }
};

export const AppPagination: FC<AppPaginationProps> = (
  props: AppPaginationProps,
) => {
  const {
    page,
    perPage,
    totalCount,
    pageSizes = [10, 50, 100],
    changePagination,
    numberOfRows,
    customClassname,
  } = props;

  const i18n = useTranslation();

  const numberOfPages = useMemo(() => {
    return Math.ceil(totalCount / perPage);
  }, [perPage, totalCount]);

  const numberOfRowsResult =
    numberOfRows ?? Math.min(perPage, totalCount - (page - 1) * perPage);

  const changePageSize = (value: number) =>
    changePagination({ page: 1, perPage: value });
  const goToFirstPage = () => changePagination({ page: 1 });
  const goToLastPage = () => {
    if (page < numberOfPages) {
      changePagination({ page: numberOfPages });
    }
  };

  if (totalCount === 0) {
    return null;
  }

  const pageCount = Math.ceil((totalCount ?? 0) / perPage);

  return (
    <div
      className={clsx(
        formStyles.rowContainer,
        formStyles.paginationWrapper,
        customClassname,
      )}
    >
      <div className={clsx(formStyles.rowContainer)}>
        <div className={formStyles.greyText}>{i18n.t('display')}</div>
        <div
          style={{ margin: '0 4px', columnGap: 4 }}
          className={clsx(formStyles.rowContainer)}
        >
          {pageSizes.map((value) => (
            <Chip
              key={value}
              value={value.toString()}
              onClickHandler={() => changePageSize(value)}
              isActive={value === perPage}
              testId={`page-size-${value}`}
            />
          ))}
        </div>
        <div className={formStyles.greyText}>{i18n.t('lines_per_page')}</div>
      </div>
      <div className={clsx(formStyles.rowContainer)}>
        <DoubleArrow
          aria-disabled={page === 1}
          onClick={goToFirstPage}
          className={clsx(styles.doubleArrow, formStyles.revertedArrow)}
          data-test-id={'pagination-first'}
        />
        <Pagination
          count={pageCount}
          page={page}
          onChange={(_, pageNumber) => {
            props.changePagination({
              page: pageNumber,
            });
          }}
          siblingCount={1}
          boundaryCount={1}
          className={styles.container}
          renderItem={(item) => (
            <PaginationItem
              slots={{
                previous: () => (
                  <Arrow
                    style={{ marginBottom: 2 }}
                    className={clsx(
                      formStyles.revertedArrow,
                      page === 1 && formStyles.disabledArrow,
                    )}
                  />
                ),
                next: () => (
                  <Arrow
                    style={{ marginBottom: -2 }}
                    className={clsx(
                      page === numberOfPages && formStyles.disabledArrow,
                    )}
                  />
                ),
              }}
              {...item}
              data-test-id={defineTestId(item)}
            />
          )}
        />
        <DoubleArrow
          onClick={goToLastPage}
          aria-disabled={page === numberOfPages}
          className={clsx(styles.doubleArrow, formStyles.pointer)}
          data-test-id={'pagination-last'}
        />
      </div>
      <div className={clsx(formStyles.text, formStyles.greyText)}>
        {i18n.t('number_of_rows')}: {numberOfRowsResult}
      </div>
    </div>
  );
};
